import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Check, Crown, Package, Book, Briefcase, Gem, Brush, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import SubscriptionBadge from '@/components/SubscriptionBadge';
import UsageCounter from '@/components/UsageCounter';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, refreshUser, nightMode } = useContext(AuthContext);
  const [currentTier, setCurrentTier] = useState('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingStatus, setFetchingStatus] = useState(true);
  const isGuestMode = user?.id === 'guest-user';
  const signupData = location.state?.signupData;

  
 useEffect(() => {
    if (!token || token === 'guest-token') {
      // Set default state for guest mode or logged-out users and skip API calls
      setCurrentTier('free');
      setSubscriptionStatus({ has_subscription: false, tier: 'free', status: null });
      setUsage({ monthly_generations: 0, limit: 5, remaining: 5 });
      setFetchingStatus(false);
      return;
    }

    const handleFocus = () => {
      fetchSubscriptionData();
    };

    handleFocus(); // Initial fetch
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [token]);

  const fetchSubscriptionData = async () => {
    try {
      setFetchingStatus(true);
      
      // First refresh user context - this updates the DB with latest Stripe status
      await refreshUser();
      
      // Fetch subscription status from Stripe (source of truth)
      const statusRes = await axios.get(`${API}/subscriptions/status`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const subStatus = statusRes.data;
      setSubscriptionStatus(subStatus);
      setCurrentTier(subStatus.tier || 'free');
      
      // Fetch usage data AFTER refreshUser updated the DB with correct tier
      const usageRes = await axios.get(`${API}/auth/usage`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(usageRes.data);
      
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      toast.error('Failed to load subscription status...');
    } finally {
      setFetchingStatus(false);
    }
  };

  const handleUpgrade = async (tier) => {
    // Free tier doesn't require payment
    if (tier === 'free') {
      toast.info('You are already on the free plan');
      return;
    }

    setLoading(true);
    try {
      // Create subscription checkout session
      const response = await axios.post(
        `${API}/subscriptions/create-checkout?tier=${tier}`,
        signupData || null,
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
      
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Failed to start checkout process');
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/subscriptions/portal`,
        { 
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Open Stripe Customer Portal
      window.location.href = response.data.url;
      
    } catch (error) {
      console.error('Failed to open portal:', error);
      toast.error('Failed to open subscription management');
      setLoading(false);
    }
  };

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: '',
      icon: Book,
      color: 'text-gray-600',
      bg: 'bg-gray-50',
      border: 'border-gray-300',
      limit: 0,
      features: [
        'Library browser mode and sharing',
        'View and vote for community stories',
        'Must upgrade to generate images/stories',
        'Inactive accounts (60 days) are deleted'
      ]
    },
    {
      id: 'starter',
      name: 'Starter Pack (one-off pack/extra credit)',
      price: '$1.99',
      futurePrice: '$2.99',
      period: '',
      icon: Package,
      color: 'text-blue-600',
      bg: 'bg-blue-250',
      border: 'border-blue-400',
      limit: 10,
      features: [
        '10 credits for images/stories, drawn after subscription credits are used',
        'Standard platform features if used standalone'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$3.99',
      futurePrice: '$5.99',
      period: '/month',
      icon: Brush,
      color: 'text-purple-600',
      bg: 'bg-purple-250',
      border: 'border-purple-400',
      limit: 20,
      popular: true,
      features: [
        '20 credits for images/stories per month',
        'Multiple child profiles',
        'Advanced features including read aloud',
        'Phonics stories'
      ]
    },
    {
      id: 'family',
      name: 'Family',
      price: '$5.99',
      futurePrice: '$8.99',
      period: '/month',
      icon: Gem,
      color: 'text-green-600',
      bg: 'bg-green-250',
      border: 'border-green-400',
      limit: 40,
      features: [
        '40 credits for stories/images per month',
        'All Premium features',
        'Custom story details',
        'Translations and read with me'
      ]
    },
    {
      id: 'folio',
      name: 'Folio Access Pass (single purchase)',
      price: '$3.99',
      futurePrice: '$5.99',
      period: '',
      icon: Briefcase,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      border: 'border-indigo-400',
      limit: 0,
      features: [
        'Access to one Folio Story Series',
        'Ability to personalise content with active Premium/Family subscription'
      ]
    }
  ];

  const platformTier = ['starter', 'folio', 'guest'].includes(currentTier) ? 'free' : currentTier;
  const currentPlatformTierIndex = tiers.findIndex(t => t.id === platformTier);
  const needsSignIn = isGuestMode || !token;

  const subscriptionTiers = tiers.filter(t => t.id === 'free' || t.period !== '');
  const addOnTiers = tiers.filter(t => t.id !== 'free' && t.period === '');

  const renderTierCard = (tier, isAddOn = false) => {
    const index = tiers.findIndex(t => t.id === tier.id);
    const isDowngrade = tier.period !== '' && index < currentPlatformTierIndex;
    const canDowngrade = user?.role === 'admin';
    const isTileDisabled = isDowngrade && !canDowngrade;

    let showBadge = false;
    let badgeText = '';

    if (!isAddOn) {
      showBadge = platformTier === tier.id;
      badgeText = '✓ ACTIVE';
    } else {
      const balance = tier.id === 'starter' ? (usage?.starter_balance ?? user?.starter_balance ?? 0) : (usage?.folio_balance ?? user?.folio_balance ?? 0);
      if (balance > 0) {
        showBadge = true;
        badgeText = `${balance} ${tier.id === 'starter' ? 'credits' : 'pass'} available`;
      }
    }

    return (
      <div
        key={tier.id}
        className={`sketch-border relative rounded-2xl ${isAddOn ? 'p-2 pb-3' : 'p-4'} ${!isTileDisabled ? 'transition-all hover:scale-105' : 'opacity-70'} ${
          (!isAddOn && platformTier === tier.id) ? 'ring-4 ring-yellow-400 shadow-2xl' : ''
        } flex flex-col h-full flex-1`}
      style={{ 
        background: isAddOn ? (nightMode ? 'rgba(251, 191, 36, 0.12)' : '#fffbeb') : 'var(--bg-secondary)',
        borderColor: isAddOn ? 'var(--accent-4)' : undefined,
        borderWidth: isAddOn ? '2px' : undefined
      }}
      >
        {tier.popular && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="sketch-border bg-yellow-400 px-4 py-1 rounded-full text-xs font-bold">
              <Crown className="w-3 h-3 inline mr-1" />
              POPULAR
            </span>
          </div>
        )}
        
        {showBadge && (
          <div className="absolute -top-4 right-4">
            <span className="sketch-border bg-green-400 px-3 py-1 rounded-full text-xs font-bold">
              {badgeText}
            </span>
          </div>
        )}

        <div className={`${isAddOn ? 'mb-2' : 'mb-4'}`}>
        <div className={`${isAddOn ? 'flex items-center justify-center gap-1.5 mb-0.5' : 'text-center mb-1.5'}`}>
          <tier.icon className={`${isAddOn ? 'w-5 h-5' : 'w-10 h-10'} ${!isAddOn ? 'mx-auto mb-1.5' : ''} ${tier.color}`} />
            <h3 className={`font-heading ${isAddOn ? 'text-base' : 'text-2xl'} font-bold ${!isAddOn ? 'mb-1' : ''}`} style={{ color: 'var(--text-primary)' }}>
              {tier.name}
            </h3>
          </div>
          
          {!isAddOn && tier.futurePrice && (
            <div className="text-center mb-2">
              <span className="text-sm font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-200">
                Introductory Price (Use code INTRO33)
              </span>
            </div>
          )}
          
          <div className="flex flex-col items-center">
            <div className="flex items-baseline justify-center gap-2 flex-wrap">
              <div className="flex items-baseline gap-1">
                <span className={`${isAddOn ? 'text-xs' : 'text-sm'}`} style={{ color: 'var(--text-secondary)' }}>
                  US
                </span>
                <span className={`${isAddOn ? 'text-xl' : 'text-4xl'} font-bold`} style={{ color: 'var(--text-primary)' }}>
                  {tier.price}
                </span>
                <span className={`${isAddOn ? 'text-xs' : 'text-sm'}`} style={{ color: 'var(--text-secondary)' }}>
                  {tier.period}
                </span>
              </div>
              {tier.futurePrice && (
                <span className={`${isAddOn ? 'text-xs' : 'text-sm'} opacity-70 line-through`} style={{ color: 'var(--text-secondary)' }}>
                  US{tier.futurePrice}{tier.period} 
                </span>
              )}
            </div>
            {!tier.futurePrice && !isAddOn && <p className="text-xs mt-1 h-4"></p>}
          </div>
        </div>

        <ul className={`${isAddOn ? 'space-y-1' : 'space-y-0.5'} mb-3 flex-grow`}>
          {tier.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-1.5">
              <Check className={`${isAddOn ? 'w-4 h-4' : 'w-5 h-5'} flex-shrink-0 mt-0.5 text-green-500`} />
              <span className={`${isAddOn ? 'text-xs' : 'text-sm'} leading-tight`} style={{ color: 'var(--text-secondary)' }}>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          onClick={() => {
            if (needsSignIn) {
              navigate('/?login=true');
            } else if (!isAddOn && platformTier === tier.id && tier.id !== 'free') {
              handleManageSubscription();
            } else {
              handleUpgrade(tier.id);
            }
          }}
          disabled={
            loading || 
            (!needsSignIn && !isAddOn && platformTier === tier.id && currentTier === 'free') || 
            (!needsSignIn && fetchingStatus) ||
            (!needsSignIn && isTileDisabled)
          }
          className={`w-full font-sketch ${isAddOn ? 'h-7 text-xs' : 'h-11 text-base'}`}
          variant={!needsSignIn && !isAddOn && platformTier === tier.id ? 'outline' : 'default'}
          style={!needsSignIn && !isAddOn && platformTier === tier.id ? {
            background: 'var(--accent-2)',
            color: 'white',
            borderColor: 'var(--accent-2)'
          } : { color: 'var(--text-primary)' }}
        >
          {loading 
            ? 'Processing...' 
            : needsSignIn
              ? 'Sign in to subscribe'
              : (!isAddOn && platformTier === tier.id) 
                ? (tier.id === 'free' 
                    ? 'Current Plan' 
                    : (tier.period === '' 
                        ? 'Buy Again' 
                        : 'Manage Subscription')) 
                      : (tier.id === 'free' ? 'Free to use' : (isAddOn ? 'Buy' : 'Subscribe'))
          }
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen page-enter" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <div className="sketch-border mx-auto max-w-7xl px-4 py-3 flex items-center justify-between" style={{ background: 'var(--bg-secondary)' }}>
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="font-sketch"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </Button>
        <div className="flex items-center gap-3">
          <span className="font-sketch text-sm" style={{ color: 'var(--text-secondary)' }}>Current Platform Subscription Plan:</span>
          <SubscriptionBadge tier={platformTier} />
          {subscriptionStatus?.status && (
            <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">
              {subscriptionStatus.status}
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="max-w-7xl mx-auto px-4 py-6 text-center">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Choose Your Plan
        </h1>
        <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
          Unlock storytelling magic for your little ones
        </p>
        
        {/* Usage Stats */}
        {!isGuestMode && token && (
          <div className="mt-4 max-w-md mx-auto transform scale-90">
            <UsageCounter />
          </div>
        )}
      </div>

      {/* Pricing Sections */}
      <div className="max-w-7xl mx-auto px-4 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 items-stretch">
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            <div className="md:col-span-3">
              <h2 className="font-heading text-lg font-bold opacity-70 mb-1" style={{ color: 'var(--text-primary)' }}>
                Platform Subscription
              </h2>
            </div>
            {subscriptionTiers.map(tier => renderTierCard(tier))}
          </div>
          
          {/* One-off Add-ons Column */}
          <div className="lg:col-span-3 flex flex-col gap-4">
            <h2 className="font-heading text-lg font-bold opacity-70 mb-1" style={{ color: 'var(--text-primary)' }}>
              Add-ons & Credit Packs
            </h2>
            <div className="flex-1 flex flex-col gap-4">
              {addOnTiers.map(tier => renderTierCard(tier, true))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;